import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Alert, Box, Button, Grid, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { api } from "../api/axios";
import { buildEmbedSnippet } from "../utils/embedSnippet";
import { PageHeader, SectionCard } from "../components/ui";

export default function InstallSnippet() {
  const { id } = useParams();
  const theme = useTheme();
  const BackIcon = theme.direction === "rtl" ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  const [site, setSite] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const snippet = useMemo(() => (site ? buildEmbedSnippet(site.site_key) : ""), [site]);

  useEffect(() => {
    api
      .get("/api/sites")
      .then(({ data }) => {
        const s = (data.sites || []).find((x) => String(x.id) === String(id));
        if (!s) throw new Error("not_found");
        setSite(s);
      })
      .catch((err) => setError(err?.response?.data?.message || err?.message || "שגיאה"));
  }, [id]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const steps = [
    { title: "העתיקו את הקוד", text: "לחצו על ״העתק Snippet״." },
    { title: "הדביקו לפני </body>", text: "בכל עמודי האתר, או דרך מנהל התגיות (GTM)." },
    { title: "בדקו שהכל עובד", text: "הריצו בדיקת הטמעה במסך ההתחלה המהירה." },
  ];

  return (
    <Box>
      <Button component={RouterLink} to={`/sites/${id}`} size="small" startIcon={<BackIcon />} sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}>
        {site?.name || "חזרה לאתר"}
      </Button>
      <PageHeader title="הטמעה" subtitle={<>הדבק את הקוד הבא לפני סגירת &lt;/body&gt; באתר שלך.</>} />

      {error ? (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <SectionCard
            title="קוד ההטמעה"
            icon={<CodeIcon />}
            action={
              <Button
                variant="contained"
                onClick={copy}
                disabled={!site}
                startIcon={copied ? <CheckRoundedIcon /> : <ContentCopyIcon />}
                color={copied ? "success" : "primary"}
              >
                {copied ? "הועתק" : "העתק Snippet"}
              </Button>
            }
          >
            {site ? (
              <Box
                component="pre"
                dir="ltr"
                tabIndex={0}
                aria-label="קוד ההטמעה"
                sx={{
                  m: 0,
                  p: 2.5,
                  borderRadius: 1.5,
                  bgcolor: "#0f172a",
                  color: "#e2e8f0",
                  border: 1,
                  borderColor: "divider",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                  fontSize: "0.8rem",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  textAlign: "start",
                  "&:focus-visible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
                }}
              >
                {snippet}
              </Box>
            ) : !error ? (
              <Skeleton variant="rounded" height={120} />
            ) : null}
            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2.5 }}>
              הקוד נטען רק אחרי שהעמוד סיים להיטען, כך שהוא לעולם לא מאט את האתר — גם אם שרת הנגישות לא זמין. אם הטמעת בעבר את
              הגרסה הישנה (<code>&lt;script src=&quot;…/embed.js&quot;&gt;</code> ללא async), החלף אותה בקוד הזה.
            </Alert>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2.5}>
            <SectionCard title="פרטי האתר" iconTone="neutral">
              {site ? (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" component="div">
                      Site Key
                    </Typography>
                    <Typography dir="ltr" sx={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", fontSize: "0.8rem", fontWeight: 600, wordBreak: "break-all", textAlign: "end" }}>
                      {site.site_key}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" component="div">
                      דומיין מאושר
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, wordBreak: "break-word" }}>{site.domain}</Typography>
                  </Box>
                </Stack>
              ) : (
                <Skeleton variant="rounded" height={70} />
              )}
            </SectionCard>
            <SectionCard title="3 צעדים פשוטים">
              <Box component="ol" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 2 }}>
                {steps.map((s, i) => (
                  <Stack component="li" key={s.title} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 26,
                        height: 26,
                        flex: "none",
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        color: "primary.main",
                        bgcolor: (t) => t.aw.accentSoft,
                      }}
                    >
                      {i + 1}
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{s.title}</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {s.text}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Box>
              <Button component={RouterLink} to={`/sites/${id}/onboarding`} variant="outlined" fullWidth startIcon={<RocketLaunchOutlinedIcon />} sx={{ mt: 2.5 }}>
                בדיקת הטמעה
              </Button>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
